import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { REST_BASE_URL } from '../app.config';
import { LanguageService } from './language.service';

/**
 * Errorcodes der Scankomponente
 */
export enum ScankomponenteErrorCodeEnum {
  SERVICE_NOT_AVAILABLE,
  UNABLE_TO_START_SCANKOMPONENTE
}

/**
 * Errortyp der Scankomponente. Arrtribute:<br>
 * errorCode = Der Fehlercode<br>
 * rootCause = Die ursrprüngliche Fehlernachricht<br>
 */
export interface ScankomponenteError {
  errorCode: ScankomponenteErrorCodeEnum;
  rootCause: any;
}

export interface ScankomponenteMockInfo {
  mockMode: boolean;
  showGuiWhenMocked: boolean;
}

export interface ScankomponenteSettings {
  resolutionDpi: number;
  compressionLevel: number;
  maxCapturedPages: number;
  autoScan: boolean;
  grayScale: boolean;
  manualSelect: boolean;
  whitelist: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ScankomponenteService {

  /**
   * Die Frontend-URL.
   */
  private static WEB_END_POINT = 'http://localhost:25000';

  /**
   * Die Basis URL zum Ansprechen der Scankomponente.
   */
  private static BASE_URL_SCAN_COMPONENT = REST_BASE_URL + '/scan-component';

  /**
   * Die URL zum Starten der Scankomponente
   */
  private static URL_SCAN_COMPONENT_BACKEND = ScankomponenteService.BASE_URL_SCAN_COMPONENT + '/start';

  /**
   * Die URL zum Abfragen für die Parameter aus dem Mockbetrieb.
   */
  private static URL_SCAN_COMPONENT_MOCK_INFO =
    ScankomponenteService.BASE_URL_SCAN_COMPONENT + '/mock-info';

  /**
   * Die URL zum Abfragen der Scanner-Einstellungen.
   */
  private static URL_SCAN_COMPONENT_SETTINGS = ScankomponenteService.BASE_URL_SCAN_COMPONENT + '/settings';

  @Output() errorReceived = new EventEmitter<ScankomponenteError>();
  @Output() mockInfoReceived = new EventEmitter<any>();

  /**
   * Boolean Flag zum bestimmen ob die Applikation im Mockbetrieb läuft, der Default ist false.
   */
  private mockMode = false;

  private showScankomponenteGuiWhenMocked = false;
  private settings: ScankomponenteSettings;

  get isMockMode(): boolean {
    return this.mockMode;
  }

  get isShowScankomponenteGui(): boolean {
    return !this.isMockMode || (this.isMockMode && this.isShowScankomponenteGuiWhenMocked);
  }

  get isShowScankomponenteGuiWhenMocked(): boolean {
    return this.showScankomponenteGuiWhenMocked;
  }

  /**
   * Constructor
   *
   * @param httpClient to ping
   * @param languageService
   */
  constructor(
    private httpClient: HttpClient,
    private languageService: LanguageService
  ) {
  }

  /**
   * Diese Methode prüft ob die Scankomponente gestartet ist und triggert
   * ggf. einen Start der Scankomponente wenn die Applikation nicht im Mockbetrieb läuft
   */
  public checkAndStartScankomponente() {
    this.getMockInfo().subscribe({
      next: response => {
        const info = response.body as ScankomponenteMockInfo;
        this.mockMode = info.mockMode;
        this.showScankomponenteGuiWhenMocked = info.showGuiWhenMocked;

        if (this.isShowScankomponenteGui) {
          this.checkScankomponente();
        }

        this.mockInfoReceived.emit();
      }, error: (error: HttpErrorResponse) => {
      }
    });
  }

  private getMockInfo(): Observable<HttpResponse<ScankomponenteMockInfo>> {
    return this.httpClient.get<ScankomponenteMockInfo>(ScankomponenteService.URL_SCAN_COMPONENT_MOCK_INFO, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }),
      observe: 'response'
    });
  }

  /**
   * Diese Methode prüft ob die Scankomponente bereits gestartet wurde, wenn nicht wird die Methode startScankomponente aufgerufen
   */
  private checkScankomponente() {
    this.httpClient.get(ScankomponenteService.WEB_END_POINT, {
      headers: new HttpHeaders({
        Accept: 'text/html'
      }),
      responseType: 'text'
    }).subscribe({
      error: () => {
        this.startScankomponente();
      }
    });
  }

  /**
   * Diese Methode startet die Scankomponente
   */
  private startScankomponente() {
    const httpParams = new HttpParams().set('language', this.languageService.getLanguage());
    this.httpClient.get(ScankomponenteService.URL_SCAN_COMPONENT_BACKEND, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }),
      params: httpParams,
      observe: 'response'
    }).subscribe({
      error: (error: HttpErrorResponse) => {
        this.errorReceived.emit({ errorCode: ScankomponenteErrorCodeEnum.UNABLE_TO_START_SCANKOMPONENTE, rootCause: error });
      }
    });
  }

  getSettings(): Observable<ScankomponenteSettings> {
    const ret: BehaviorSubject<ScankomponenteSettings> = new BehaviorSubject<ScankomponenteSettings>(this.getDefaultSettings());

    ret.getValue().compressionLevel = this.settings.compressionLevel;
    ret.getValue().maxCapturedPages = this.settings.maxCapturedPages;
    ret.getValue().resolutionDpi = this.settings.resolutionDpi;
    ret.getValue().autoScan = this.settings.autoScan;
    ret.getValue().grayScale = this.settings.grayScale;
    ret.getValue().manualSelect = this.settings.manualSelect;
    ret.getValue().whitelist = this.settings.whitelist;

    return ret;
  }

  fetchSettings(): Observable<ScankomponenteSettings> {
    return this.httpClient.get<ScankomponenteSettings>(
      ScankomponenteService.URL_SCAN_COMPONENT_SETTINGS,
      { params: new HttpParams().set('language', this.languageService.getLanguage()).set('profile', 'visa') }
    ).pipe(tap({
      next: (result) => {
        this.settings = result;
      },
      error: error => {
        console.error('Die Scanner-Einstellungen konnten nicht ermittelt werden.');
        console.error(error.error.message);
        this.settings = this.getDefaultSettings();
        console.error('Es werden die Standardwerte verwendet : ' + this.settings);
      }
    }));
  }

  private getDefaultSettings(): ScankomponenteSettings {
    return {
      compressionLevel: 50,
      maxCapturedPages: 35,
      resolutionDpi: 100,
      autoScan: true,
      grayScale: true,
      manualSelect: false,
      whitelist: []
    } as ScankomponenteSettings;
  }

}
